import { useState } from 'react';
import { dtoVersConge } from '../../api/mappers';
import { congesApi } from '../../api/resources';
import { C } from '../../theme/palette';

const fd = (iso) => {
  if (!iso) return '';
  const z = ('' + iso).split('-');
  return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
};

/**
 * Fenêtre « 📅 Proposer une autre date » d'une demande de congé.
 *
 * Plutôt que refuser des vacances tombant en plein chantier, la direction en
 * propose d'autres ; l'employé accepte ou refuse dans son espace. La
 * proposition part au serveur, qui fait passer la demande à « Date
 * proposée ».
 *
 * @param {object} props propriétés de la fenêtre
 * @param {object} props.conge demande concernée
 * @param {string} [props.nomEmploye] nom de l'employé
 * @param {(fn: (p: object[]) => object[]) => void} props.setConges mise à jour des demandes
 * @param {() => void} props.onClose fermeture
 * @returns {import('react').ReactElement} la fenêtre
 */
export function ProposerDate({ conge, nomEmploye, setConges, onClose }) {
  const [debut, setDebut] = useState(conge.debut || '');
  const [fin, setFin] = useState(conge.fin || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const propose = async () => {
    if (!debut || !fin) return;
    if (fin < debut) {
      setErreur('La date de fin doit suivre la date de début.');
      return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      const dto = await congesApi.propose(conge.id, debut, fin);
      setConges((p) => p.map((x) => (x.id === conge.id ? dtoVersConge(dto) : x)));
      onClose();
    } catch (e) {
      setErreur(e?.message || 'La proposition n’a pas pu être envoyée.');
    } finally {
      setEnvoi(false);
    }
  };

  const champ = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid ' + C.border,
    borderRadius: 9,
    padding: 11,
    fontSize: 14,
  };
  const etiquette = {
    fontSize: 10,
    fontWeight: 700,
    color: C.muted,
    marginBottom: 4,
    textTransform: 'uppercase',
    display: 'block',
  };
  const inactif = !debut || !fin || envoi;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-label="Proposer une autre date"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, padding: 18, width: '100%', maxWidth: 380 }}
      >
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>
          📅 Proposer une autre date
        </div>
        <div style={{ fontSize: 11, color: C.grey, marginBottom: 13 }}>
          À {nomEmploye || 'l’employé'} · demandé : {fd(conge.debut)} → {fd(conge.fin)}
        </div>
        <label style={etiquette}>
          Nouveau début
          <input
            type="date"
            value={debut}
            onChange={(e) => setDebut(e.target.value)}
            style={{ ...champ, marginTop: 4, marginBottom: 11 }}
          />
        </label>
        <label style={etiquette}>
          Nouvelle fin
          <input
            type="date"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
            style={{ ...champ, marginTop: 4, marginBottom: 15 }}
          />
        </label>
        {erreur && (
          <div role="alert" style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>
            {erreur}
          </div>
        )}
        <div style={{ display: 'flex', gap: 9 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: 12,
              background: C.navyXL,
              border: 'none',
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 800,
              color: C.grey,
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={inactif}
            onClick={propose}
            style={{
              flex: 2,
              padding: 12,
              background: inactif ? C.border : C.navy,
              border: 'none',
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 800,
              color: '#fff',
              cursor: inactif ? 'default' : 'pointer',
            }}
          >
            ✓ Proposer cette date
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Lien « 📎 Voir le certificat » d'une demande, pour la direction.
 *
 * @param {object} props propriétés du lien
 * @param {object} props.conge demande concernée
 * @returns {import('react').ReactElement|null} le lien, ou rien sans certificat
 */
export function LienCertificat({ conge }) {
  if (!conge.certificat || typeof conge.id !== 'number') return null;
  return (
    <a
      href={congesApi.adresseCertificat(conge.id)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 11, fontWeight: 700, color: C.purple, textDecoration: 'none' }}
    >
      📎 Voir le certificat
    </a>
  );
}
